/**
 * GraphQL queries and hooks — the Roadmap & Change Widgets dashboard widgets
 * added by issue #1818 (successor to #1656, part of epic #1650): Active
 * Initiatives and Roadmap Timeline. The `activeInitiatives`/`roadmapTimeline`
 * queries themselves were built by the prerequisite issue #1746 (see
 * `backend/src/graphql/resolvers/changeInitiatives.ts` and `roadmaps.ts`);
 * this module is only the frontend query/hook wiring #1746 explicitly left
 * out of scope.
 */

import { gql } from '@apollo/client';
import { useQuery, type QueryResult } from '@apollo/client/react';

/**
 * Query for the Active Initiatives widget (issue #1818). `impactsMyServices`
 * and `statuses` are both optional — the server defaults `statuses` to
 * `proposed`/`approved`/`in-progress` when omitted. The widget itself passes
 * no arguments, taking the server defaults.
 */
export const GET_ACTIVE_INITIATIVES = gql`
  query GetActiveInitiatives($impactsMyServices: Boolean, $statuses: [String!]) {
    activeInitiatives(impactsMyServices: $impactsMyServices, statuses: $statuses) {
      initiatives {
        id
        name
        status
        startDate
        endDate
        owner
        ownerName
        impactedServicesCount
        myServicesImpacted
      }
    }
  }
`;

export interface ActiveInitiativeEntry {
  id: string;
  name: string;
  status: string | null;
  startDate: string | null;
  endDate: string | null;
  owner: string | null;
  ownerName: string | null;
  impactedServicesCount: number;
  myServicesImpacted: number;
}

export interface ActiveInitiativesData {
  activeInitiatives: {
    initiatives: ActiveInitiativeEntry[];
  };
}

export interface ActiveInitiativesVariables {
  impactsMyServices?: boolean;
  statuses?: string[];
}

/** Hook for the Active Initiatives widget's initiative list. */
export function useActiveInitiatives(
  variables?: ActiveInitiativesVariables
): QueryResult<ActiveInitiativesData, ActiveInitiativesVariables> {
  return useQuery(GET_ACTIVE_INITIATIVES, {
    variables: variables ?? {},
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

/**
 * Query for the Roadmap Timeline widget (issue #1818). `roadmapId` narrows
 * to a single roadmap; omitted, the server returns every roadmap. The
 * widget itself passes no arguments.
 */
export const GET_ROADMAP_TIMELINE = gql`
  query GetRoadmapTimeline($roadmapId: ID) {
    roadmapTimeline(roadmapId: $roadmapId) {
      id
      name
      horizonStart
      horizonEnd
      scope
      initiatives {
        id
        name
        status
        startDate
        endDate
      }
    }
  }
`;

export interface RoadmapInitiativeEntry {
  id: string;
  name: string;
  status: string | null;
  startDate: string | null;
  endDate: string | null;
}

export interface RoadmapTimelineEntry {
  id: string;
  name: string;
  horizonStart: string | null;
  horizonEnd: string | null;
  scope: string | null;
  initiatives: RoadmapInitiativeEntry[];
}

export interface RoadmapTimelineData {
  roadmapTimeline: RoadmapTimelineEntry[];
}

export interface RoadmapTimelineVariables {
  roadmapId?: string;
}

/** Hook for the Roadmap Timeline widget's per-roadmap initiative list. */
export function useRoadmapTimeline(
  roadmapId?: string
): QueryResult<RoadmapTimelineData, RoadmapTimelineVariables> {
  return useQuery(GET_ROADMAP_TIMELINE, {
    variables: { roadmapId },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}
